import {
  Text,
  SelectField,
  InlineRadioField,
  Alert,
} from '@cinch-labs/design-system'
import { useEffect, useState } from 'react'
import styles from './select-date-time.module.css'
import { useMaintenanceContext } from '../../context/maintenance-context'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import { addDays, addMonths, eachDayOfInterval, format } from 'date-fns'
import { AppointmentType } from '../../types'
import { useFetchAppointmentAvailability } from '../../hooks/fetch-appointment-availability'
import { LoadingSpinner } from '@cinch-nx/shared-ui'

export const SelectDateTime: React.FC = () => {
  const [appointmentType, setAppointmentType] =
    useState<AppointmentType>('Drop and go')
  const [selectedDate, setSelectedDate] = useState<string | undefined>()
  const [selectedTime, setSelectedTime] = useState<string | undefined>()
  const [appointmentTypeChanged, setAppointmentTypeChanged] = useState(false)
  const { trackEvent } = useAnalytics()

  const {
    vehicleMake,
    vehicleModel,
    vrm,
    store,
    displayName,
    dispatch,
    step,
    serviceOptionsSelectedNames,
    appointmentType: preselectedAppointmentType,
    bookingDate,
    bookingTime,
    canBookAppointment,
    dealerId,
    isNonCag,
  } = useMaintenanceContext()

  const {
    fetchAppointmentDates,
    fetchAppointmentTimes,
    appointmentDates,
    appointmentTypeId,
    timeSlots,
    loading,
    timeSlotsLoading,
    errorType,
  } = useFetchAppointmentAvailability()

  useEffect(() => {
    if (preselectedAppointmentType !== undefined) {
      setAppointmentType(preselectedAppointmentType)
    }
  }, [preselectedAppointmentType])

  useEffect(() => {
    if (canBookAppointment) {
      fetchAppointmentDates(
        dealerId,
        serviceOptionsSelectedNames,
        appointmentType,
        displayName,
      )
    }
  }, [appointmentType])

  useEffect(() => {
    setAppointmentTypeChanged(false)
    if (appointmentDates && appointmentDates.length > 0) {
      fetchAppointmentTimes(
        dealerId,
        appointmentTypeId,
        appointmentDates[0],
        displayName,
      )
    }
  }, [appointmentDates])

  const handleSelectDate = async (date: string) => {
    setAppointmentTypeChanged(false)
    setSelectedDate(date)
    fetchAppointmentTimes(dealerId, appointmentTypeId, date, displayName)
  }

  const dropOffDates = getDateOptions(isNonCag)

  const dropOffTimes = [
    { label: '9:00am', value: '9:00am' },
    { label: '9:30am', value: '9:30am' },
    { label: '10:00am', value: '10:00am' },
    { label: '10:30am', value: '10:30am' },
    { label: '11:00am', value: '11:00am' },
    { label: '11:30am', value: '11:30am' },
    { label: '12:00pm', value: '12:00pm' },
    { label: '12:30pm', value: '12:30pm' },
    { label: '1:00pm', value: '1:00pm' },
    { label: '1:30pm', value: '1:30pm' },
    { label: '2:00pm', value: '2:00pm' },
  ]

  if (isNonCag) {
    dropOffTimes.unshift(
      { label: '8:00am', value: '8:00am' },
      { label: '8:30am', value: '8:30am' },
    )
  }

  const handleContinue = () => {
    const selectDateTime = {
      eventName: `Select date time`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'drop_off_selected',
          label: canBookAppointment ? 'booking_journey' : 'enquiry_journey',
        },
      },
    }

    trackEvent(selectDateTime)

    datadogRum.addAction('Cinch Servicing: DateTime Selected', {
      cinchServicing: true,
      step,
      page: window.location.pathname,
      serviceOptions: serviceOptionsSelectedNames,
      selectedDate: selectedDate ?? bookingDate ?? dropOffDates[0].value,
      selectedTime:
        appointmentType === 'Drop and wait'
          ? selectedTime ?? bookingTime ?? 'AM'
          : selectedTime ?? bookingTime ?? dropOffTimes[0].value,
      store,
      canBookAppointment,
    })

    dispatch({
      type: 'SET_BOOKING_DATETIME',
      appointmentType,
      bookingDate: canBookAppointment
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          selectedDate ?? bookingDate ?? appointmentDates![0]
        : selectedDate ?? bookingDate ?? dropOffDates[0].value,
      bookingTime: canBookAppointment
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          selectedTime ?? bookingTime ?? timeSlots![0]?.timeIn
        : appointmentType === 'Drop and wait'
        ? selectedTime ?? bookingTime ?? 'AM'
        : selectedTime ?? bookingTime ?? dropOffTimes[0].value,
      appointmentTypeId,
    })
  }

  const title = canBookAppointment
    ? 'Book your appointment'
    : 'Choose a preferred appointment option'
  const description = `For your ${vehicleMake} ${vehicleModel} (${vrm}) ${serviceOptionsSelectedNames}${
    displayName ? ` at ${displayName}` : ''
  }.`

  return (
    <CarMaintenanceStepWrapper
      title={title}
      description={description}
      showContinueButton={
        !canBookAppointment || (timeSlots && timeSlots.length > 0) || false
      }
      onContinue={handleContinue}
    >
      <div className={styles.timesSection}>
        {!canBookAppointment && (
          <Text
            element="h2"
            fontSizeFixed="m"
            fontWeight="bold"
            className={styles.chooseLabel}
          >
            Choose an appointment option
          </Text>
        )}

        <InlineRadioField
          label="Appointment type"
          data-testid="appointmentType"
          options={[
            { id: 'dropAndGo', value: 'Drop and go', label: 'Drop and go' },
            {
              id: 'dropAndWait',
              value: 'Drop and wait',
              label: 'Drop and wait',
            },
          ]}
          name={'appointment type'}
          value={preselectedAppointmentType ?? appointmentType}
          onChange={(e) => {
            setAppointmentType(e.target.value as AppointmentType)
            setAppointmentTypeChanged(true)
          }}
          classNames={{
            container: styles.inlineRadioFieldContainer,
            radio: { label: styles.inlineRadioLabel },
            error: { container: styles.error },
          }}
        />

        {canBookAppointment ? (
          <>
            {errorType === 'api_error' && !loading && (
              <Alert type="error" className={styles.errorAlert}>
                <Text fontSizeFixed="s">
                  Sorry, something went wrong with loading appointment times.
                  Please reload the page and try again.
                </Text>
              </Alert>
            )}
            {errorType !== 'api_error' && (
              <>
                <div>
                  {loading && <LoadingSpinner />}
                  {!loading &&
                    appointmentDates &&
                    appointmentDates.length > 0 && (
                      <SelectField
                        id="appointment-date"
                        label={'Date'}
                        options={transformDates(appointmentDates)}
                        onChange={(e) => {
                          handleSelectDate(e.target.value)
                        }}
                        classNames={{
                          container: styles.selectContainer,
                        }}
                        error={{
                          show: false,
                          collapse: true,
                        }}
                        defaultValue={bookingDate}
                      />
                    )}
                </div>
                {(errorType === 'no_dates_available' ||
                  errorType === 'no_time_slots_available') &&
                  !loading &&
                  !timeSlotsLoading && (
                    <Alert type="error" className={styles.errorAlert}>
                      <Text fontSizeFixed="s">
                        Sorry, we can't find any available slots. Please try a
                        different appointment type or date.
                      </Text>
                    </Alert>
                  )}
                <div>
                  {timeSlotsLoading && <LoadingSpinner />}
                  {!timeSlotsLoading && (
                    <div>
                      {timeSlots &&
                        timeSlots.length > 0 &&
                        !appointmentTypeChanged && (
                          <SelectField
                            id="appointment-time"
                            label={'Time'}
                            options={timeSlots.map((timeSlot) => ({
                              label: timeSlot.timeIn,
                              value: timeSlot.timeIn,
                            }))}
                            onChange={(e) => {
                              setSelectedTime(e.target.value)
                            }}
                            classNames={{
                              container: styles.selectContainer,
                            }}
                            error={{
                              show: false,
                              collapse: true,
                            }}
                            defaultValue={bookingTime}
                          />
                        )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : appointmentType === 'Drop and wait' ? (
          <div className={styles.dateTimeSelectContainer}>
            <Text
              element="h2"
              fontSizeFixed="m"
              fontWeight="bold"
              className={styles.chooseLabel}
            >
              Choose a preferred time
            </Text>

            <Alert type="info" className={styles.alertDropWait}>
              <Text fontSizeFixed="s">
                Appointment dates and times aren’t guaranteed, but we’ll get in
                touch to confirm a specific timeslot
              </Text>
            </Alert>

            <SelectField
              id="appointment-date"
              label={'Preferred date'}
              options={dropOffDates}
              onChange={(e) => {
                setSelectedDate(e.target.value)
              }}
              classNames={{
                container: styles.selectContainer,
              }}
              error={{
                show: false,
                collapse: true,
              }}
              defaultValue={bookingDate}
            />

            <div className={styles.preferredTimeSection}>
              <Text
                element="label"
                fontSizeFixed="s"
                fontWeight="bold"
                className={styles.preferredTimeLabel}
              >
                Preferred time
              </Text>
              <Text fontSizeFixed="xs" className={styles.preferredTimeSubtext}>
                We’ll get in touch to confirm a specific timeslot
              </Text>
            </div>

            <InlineRadioField
              label=""
              data-testid="preferredTimeAmPm"
              options={[
                { id: 'am', value: 'AM', label: 'AM' },
                { id: 'pm', value: 'PM', label: 'PM' },
              ]}
              name={'preferred time'}
              value={selectedTime ?? bookingTime ?? 'AM'}
              onChange={(e) => {
                setSelectedTime(e.target.value)
              }}
              classNames={{
                container: styles.amPmRadioContainer,
                radio: { label: styles.amPmRadioLabel },
                error: { container: styles.error },
              }}
            />
          </div>
        ) : (
          <div className={styles.dateTimeSelectContainer}>
            <SelectField
              id="appointment-date"
              label={'Preferred appointment date'}
              options={dropOffDates}
              onChange={(e) => {
                setSelectedDate(e.target.value)
              }}
              classNames={{
                container: styles.selectContainer,
              }}
              error={{
                show: false,
                collapse: true,
              }}
              defaultValue={bookingDate}
            />
            <SelectField
              id="appointment-time"
              label={'Preferred drop-off time'}
              options={dropOffTimes}
              onChange={(e) => {
                setSelectedTime(e.target.value)
              }}
              classNames={{
                container: styles.selectContainerTime,
                selectWrapper: styles.selectWrapper,
              }}
              error={{
                show: false,
                collapse: true,
              }}
              defaultValue={bookingTime}
            />

            <Alert type="info" className={styles.alert}>
              <Text fontSizeFixed="s">
                Appointment date/times aren’t guaranteed but we’ll do our best
                to accommodate you.
              </Text>
            </Alert>
          </div>
        )}
      </div>
    </CarMaintenanceStepWrapper>
  )
}

export const getDateOptions = (excludeSaturday?: boolean) => {
  let firstAvailableDate = addDays(new Date(), 3)

  //if today is Thurs, Fri or Sat then need to add 4 because services cannot be booked on a Sunday.
  if (new Date().getDay() >= 5 && new Date().getDay() <= 7) {
    firstAvailableDate = addDays(new Date(), 4)
  }

  const lastAvailableDate = addMonths(new Date(), 3)

  const allAvailableDates = eachDayOfInterval({
    start: firstAvailableDate,
    end: lastAvailableDate,
  }).map((date) => ({
    label: format(date, 'ccc dd MMM yyyy'),
    value: format(date, 'ccc dd MMM yyyy'),
  }))

  return allAvailableDates.filter((date) => {
    if (excludeSaturday) {
      return !date.label.includes('Sun') && !date.label.includes('Sat')
    }
    return !date.label.includes('Sun')
  })
}

const transformDates = (dates: string[] | undefined) =>
  !dates
    ? [{ label: '', value: '' }]
    : dates.map((date: string) => ({
        label: format(new Date(date), 'ccc dd MMM yyyy'),
        value: date,
      }))
